import type {Middleware} from 'redux';
import * as rrweb from 'rrweb';
import {isRecordingActive} from '../lib/sessionReplay';
import type {TrackedActionPayload} from './actionSummaryTypes';
import {accumulateAction} from './sessionSummaryAccumulator';

/** Entity types we track, with their payload key and display name field */
const TRACKED_ENTITIES: Record<string, { payloadKey: string; nameField: string; label: string }> = {
	MATTER:      {payloadKey: 'matter',      nameField: 'name',          label: 'Cases'},
	CONTACT:     {payloadKey: 'contact',     nameField: 'fml',           label: 'Contacts'},
	TASK:        {payloadKey: 'task',         nameField: 'title',         label: 'Tasks'},
	LEAD:        {payloadKey: 'lead',        nameField: 'name',          label: 'Leads'},
	DOCUMENT:    {payloadKey: 'document',    nameField: 'fileName',      label: 'Documents'},
	INVOICE:     {payloadKey: 'invoice',     nameField: 'invoiceNumber', label: 'Invoices'},
	TRANSACTION: {payloadKey: 'transaction', nameField: 'id',            label: 'Payments'},
	EVENT:       {payloadKey: 'event',       nameField: 'title',         label: 'Events'},
	EXPENSE:     {payloadKey: 'expense',     nameField: 'description',   label: 'Expenses'},
	COMMENT:     {payloadKey: 'comment',     nameField: 'body',          label: 'Comments'},
};

const SAVE_SUCCESS = '_SAVE_SUCCESS';
const REMOVE_SUCCESS = '_REMOVE_SUCCESS';
const RESTORE_SUCCESS = '_RESTORE_SUCCESS';
const SAVE_MANY_SUCCESS = '_SAVE_MANY_SUCCESS';

function getOperation(actionType: string, entity: any): 'C' | 'U' | 'D' | 'R' {
	if (actionType.endsWith(REMOVE_SUCCESS)) return 'D';
	if (actionType.endsWith(RESTORE_SUCCESS)) return 'R';
	if (entity?.status === 'C') return 'C';
	return 'U';
}

function getDisplayName(entity: any, nameField: string, entityPrefix: string): string {
	if (!entity) return 'unknown';

	if (entityPrefix === 'COMMENT') {
		const body = entity.body;
		return typeof body === 'string' ? body.slice(0, 50) : String(entity.id || 'unknown');
	}

	if (entityPrefix === 'EXPENSE') {
		return entity.description || String(entity.id || 'unknown').slice(0, 8);
	}

	if (entityPrefix === 'TRANSACTION') {
		return String(entity.id || 'unknown').slice(0, 8);
	}

	const value = entity[nameField];
	return typeof value === 'string' ? value : String(entity.id || 'unknown');
}

function parseActionType(actionType: string): { prefix: string; suffix: string } | null {
	for (const suffix of [SAVE_MANY_SUCCESS, SAVE_SUCCESS, REMOVE_SUCCESS, RESTORE_SUCCESS]) {
		if (actionType.endsWith(suffix)) {
			return {prefix: actionType.slice(0, -suffix.length), suffix};
		}
	}
	return null;
}

function emitAction(entity: any, entityPrefix: string, config: typeof TRACKED_ENTITIES[string], actionType: string): void {
	const payload: TrackedActionPayload = {
		t: config.payloadKey,
		i: String(entity?.id || ''),
		n: getDisplayName(entity, config.nameField, entityPrefix),
		o: getOperation(actionType, entity),
	};
	rrweb.addCustomEvent('a', payload);
	accumulateAction(config.payloadKey, payload.o);
}

export const actionTrackingMiddleware: Middleware = _store => next => (action: any) => {
	const result = next(action);

	if (!isRecordingActive()) return result;

	const actionType = action?.type;
	if (typeof actionType !== 'string') return result;

	const parsed = parseActionType(actionType);
	if (!parsed) return result;

	const config = TRACKED_ENTITIES[parsed.prefix];
	if (!config) return result;

	if (parsed.suffix === SAVE_MANY_SUCCESS) {
		// Plural payload key (e.g., 'matters', 'tasks')
		const pluralKey = config.payloadKey + 's';
		const entities = action[pluralKey];
		if (Array.isArray(entities)) {
			for (const entity of entities) {
				emitAction(entity, parsed.prefix, config, actionType);
			}
		}
	} else {
		const entity = action[config.payloadKey];
		if (entity) {
			emitAction(entity, parsed.prefix, config, actionType);
		}
	}

	return result;
};
